import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { client, useEvent } from '../';

import { EventSettings } from './types';
import {
    reshapeLocations,
    patchDatesLabelMapping,
    makeEventDaysTimestampPreferredTimezone,
} from './reshapeSettings';

const DEFAULT_SETTINGS = {
    datesLabelType: 'DATES' as const,
    datesLabelMapping: {},
    hideDatesWithNoSessions: false,
    filters: [],
    locations: [],
};

type UseEventSettingsType = (eventUniqueName: string) => {
    data: EventSettings;
    error: Error;
    isLoading: boolean;
    isSuccess: boolean;
};

export async function fetchEventSettings(eventId: number) {
    return (await client().get(`/api/v2/agenda/events/${eventId}/venue/settings`))
        .data as EventSettings;
}

const useRawEventSettings = eventUniqueName => {
    const eventQuery = useEvent(eventUniqueName);

    return useQuery<EventSettings, Error>(
        ['settings', eventQuery?.data?.id],
        () => fetchEventSettings(eventQuery?.data?.id),
        { enabled: !!eventQuery?.data?.id }
    );
};
const useEventSettings: UseEventSettingsType = eventUniqueName => {
    const settingsQuery = useRawEventSettings(eventUniqueName);
    return useMemo(() => {
        if (settingsQuery.isLoading || settingsQuery.isIdle) {
            return {
                data: DEFAULT_SETTINGS,
                error: settingsQuery.error,
                isLoading: settingsQuery.isLoading,
                isSuccess: settingsQuery.isSuccess,
                isIdle: settingsQuery.isIdle,
            };
        }

        const settings = (settingsQuery?.data || DEFAULT_SETTINGS) as EventSettings; // API response can be empty string

        return {
            data: {
                ...settings,
                datesLabelMapping: undefined, // deprecated field, use useEventDatesLabels
                locations: settings.locations ? reshapeLocations(settings.locations) : {},
            },
            error: settingsQuery.error,
            isLoading: settingsQuery.isLoading,
            isSuccess: settingsQuery.isSuccess,
        };
    }, [settingsQuery]);
};

type useEventDatesLabelsType = (
    eventUniqueName: string,
    preferredTimezone?: string
) => {
    data: Record<string, string>;
    error: Error;
    isLoading: boolean;
    isSuccess: boolean;
};

export const useEventDatesLabels: useEventDatesLabelsType = (
    eventUniqueName,
    preferredTimezone
) => {
    const eventQuery = useEvent(eventUniqueName);
    const settingsQuery = useRawEventSettings(eventUniqueName);
    return useMemo(() => {
        if (
            eventQuery.isIdle ||
            eventQuery.isLoading ||
            eventQuery.error ||
            settingsQuery.isLoading ||
            settingsQuery.isIdle
        ) {
            return {
                data: {},
                error: settingsQuery.error,
                isLoading: settingsQuery.isLoading,
                isSuccess: settingsQuery.isSuccess,
                isIdle: eventQuery.isIdle || settingsQuery.isIdle,
            };
        }

        const settings = (settingsQuery?.data || DEFAULT_SETTINGS) as EventSettings; // API response can be empty string
        const { datesLabelMapping } = settings;
        const { startDate, endDate } = eventQuery.data;

        const eventDaysTimestampsUserTimezone = makeEventDaysTimestampPreferredTimezone(
            startDate,
            endDate,
            preferredTimezone
        );

        return {
            data: patchDatesLabelMapping(eventDaysTimestampsUserTimezone, datesLabelMapping) || {},
            error: settingsQuery.error,
            isLoading: settingsQuery.isLoading,
            isSuccess: settingsQuery.isSuccess,
        };
    }, [eventQuery, settingsQuery, preferredTimezone]);
};

export default useEventSettings;
